import React, { useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, FolderOpen, Save, Search, ListPlus } from "lucide-react";
import { toast } from "sonner";
import { deleteConfirmMessage } from "@/lib/libraryDelete";
import { withBaseRevision } from "@/lib/recordRevision";
import { saveRefusal, copyName, refusalText, missingMessage } from "@/lib/saveConflict";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Combined save / load dialog.
// mode is one of:
//   'save-activity'      → upsert current tab into the Activity Library
//   'save-activity-new'  → create a fresh Library entry (new id)
//   'save-session'       → upsert the whole workspace as a Session
//   'save-session-new'   → create a fresh Session (new id)
//   'load'               → browse both Libraries and pick one to load
export const SessionsDialog = ({
  open,
  onOpenChange,
  mode,
  activitySnapshot,   // { id, name, ...activity+board } — current active tab
  sessionSnapshot,    // { id, name, activities:[...] }
  activityRevision,   // NP-4: revision of the open Library activity (null = none)
  sessionRevision,    // NP-4: revision of the open Session (null = none)
  isSessionOpen,      // true if the current workspace is a saved Session
  onLoadActivity,
  onLoadSession,
  onAddActivityToSession,
  onRecordDeleted,    // (kind, id) after a Library record is deleted
  onSignInExpired,    // sign-in ran out and could not be renewed (NP-2)
}) => {
  const isSave = mode && mode.startsWith("save-");
  const kind = mode === "save-session" || mode === "save-session-new" ? "session" : "activity";
  const modeIsNew = mode === "save-activity-new" || mode === "save-session-new";

  const [name, setName] = useState("");
  const [libraryKind, setLibraryKind] = useState("activity"); // load-mode tab
  const [activities, setActivities] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  // NP-4 Step 5: the server refused a normal save because the record was
  // saved elsewhere since it was opened (409). The dialog stays open with
  // two choices: Save as copy (→ the Save As New flow) or Cancel.
  // NP-4 Step 7: also when the server cannot confirm the version (428).
  const [conflict, setConflict] = useState(false); // false | "conflict" | "unconfirmed"
  const [saveAsCopy, setSaveAsCopy] = useState(false); // Save as copy chosen
  const isNew = modeIsNew || saveAsCopy;

  // NP-2: a request failed because the sign-in expired and could not be
  // renewed. Say so plainly and hand over to the sign-in flow (the parent
  // keeps the pending action so it resumes after signing in again).
  const handleSignInExpired = (e, action) => {
    if (!e || !e.sessionExpired) return false;
    toast.error(`Your sign-in has expired. Sign in again to ${action} — your work is safe.`);
    if (onSignInExpired) onSignInExpired();
    return true;
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [ar, sr] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/sessions`),
      ]);
      setActivities(ar.data || []);
      setSessions(sr.data || []);
    } catch (e) {
      if (handleSignInExpired(e, "open your Library")) return;
      toast.error("Couldn't load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (mode === "load") {
      refresh();
      setSearch("");
      setLibraryKind("activity");
    }
    if (isSave) {
      const base = kind === "session"
        ? (sessionSnapshot?.name || "")
        : (activitySnapshot?.name || "");
      // "Save As New" pre-fills a fresh name so the coach knows it's a copy.
      setName(modeIsNew && base ? `${base} Copy` : base);
      setConflict(false);
      setSaveAsCopy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const filteredList = useMemo(() => {
    const list = libraryKind === "session" ? sessions : activities;
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => (a.name || "").toLowerCase().includes(q));
  }, [libraryKind, activities, sessions, search]);

  const doSaveActivity = async () => {
    // Save As New wipes the id so the backend creates a fresh record; the
    // regular Save preserves the current activityId when present.
    // NP-4: a normal update sends the revision it is based on; Save As New
    // (an insert) never does.
    const payload = withBaseRevision({
      ...activitySnapshot,
      id: isNew ? undefined : (activitySnapshot?.id || undefined),
      name: name.trim(),
      canonicalVersion: 2,
    }, { isNew, revision: activityRevision });
    const res = await axios.post(`${API}/activities`, payload);
    toast.success(`Saved "${res.data.name}"`);
    // Return the persisted record so the parent can adopt the new id.
    return { kind: "activity", record: res.data };
  };

  const doSaveSession = async () => {
    const payload = withBaseRevision({
      id: isNew ? undefined : (sessionSnapshot?.id || undefined),
      name: name.trim(),
      // Forward every session-level metadata field so the backend persists
      // it alongside the embedded activities.
      date: sessionSnapshot?.date || "",
      startTime: sessionSnapshot?.startTime || "",
      location: sessionSnapshot?.location || "",
      objective: sessionSnapshot?.objective || "",
      load: sessionSnapshot?.load || "",
      offensivePrinciples: sessionSnapshot?.offensivePrinciples || [],
      defensivePrinciples: sessionSnapshot?.defensivePrinciples || [],
      // Embed a complete independent snapshot of each tab. Strip the
      // activityId from every embedded activity so mutating the session
      // never writes back into a Library entry.
      activities: (sessionSnapshot?.activities || []).map((a) => ({
        ...a,
        activityId: null,
      })),
    }, { isNew, revision: sessionRevision }); // NP-4, as for activities
    const res = await axios.post(`${API}/sessions`, payload);
    toast.success(`Saved session "${res.data.name}"`);
    return { kind: "session", record: res.data };
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(`Please enter a ${kind} name`);
      return;
    }
    setSaving(true);
    try {
      const result = kind === "session" ? await doSaveSession() : await doSaveActivity();
      onOpenChange(false, result);
    } catch (e) {
      if (handleSignInExpired(e, "save")) return;
      // NP-4 Step 5: a refused save wrote nothing. Keep the dialog open and
      // the workspace untouched; never retry, overwrite or recreate.
      const refusal = saveRefusal(e);
      if (refusal === "conflict" || refusal === "unconfirmed") {
        setConflict(refusal);
        return;
      }
      if (refusal === "missing") {
        toast.error(missingMessage(kind, name));
        return;
      }
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Save as copy: continue in the existing Save As New flow (an insert, no
  // baseRevision), with the name pre-filled as "<name> Copy".
  const handleSaveAsCopy = () => {
    setConflict(false);
    setSaveAsCopy(true);
    setName(copyName(name));
  };

  const handleLoadClick = async (id) => {
    try {
      if (libraryKind === "session") {
        const res = await axios.get(`${API}/sessions/${id}`);
        // `false` = the coach cancelled (unsaved work kept) — no toast, dialog stays.
        if (onLoadSession(res.data) === false) return;
        toast.success(`Loaded session "${res.data.name}"`);
      } else {
        const res = await axios.get(`${API}/activities/${id}`);
        if (onLoadActivity(res.data) === false) return;
        toast.success(`Loaded "${res.data.name}"`);
      }
      onOpenChange(false);
    } catch (e) {
      if (handleSignInExpired(e, "load")) return;
      toast.error("Load failed");
    }
  };

  // Library deletes are permanent: always confirm, naming the record.
  const handleDelete = async (item, evt) => {
    evt.stopPropagation();
    const id = item.id;
    const isSession = libraryKind === "session";
    const message = deleteConfirmMessage({
      kind: isSession ? "session" : "activity",
      name: item.name,
      activityCount: (item.activities || []).length,
      isOpen: isSession && !!sessionSnapshot?.id && sessionSnapshot.id === id,
    });
    if (!window.confirm(message)) return;
    try {
      const path = isSession ? "sessions" : "activities";
      await axios.delete(`${API}/${path}/${id}`);
      if (isSession) setSessions((cur) => cur.filter((s) => s.id !== id));
      else setActivities((cur) => cur.filter((a) => a.id !== id));
      if (onRecordDeleted) onRecordDeleted(isSession ? "session" : "activity", id);
      toast.success("Deleted");
    } catch (e) {
      if (handleSignInExpired(e, "delete")) return;
      toast.error("Delete failed");
    }
  };

  const title = isSave
    ? (kind === "session"
        ? (isNew ? "Save Session As New" : "Save Session")
        : (isNew ? "Save Activity As New" : "Save Activity"))
    : "Library";

  return (
    <Dialog open={open} onOpenChange={(v) => onOpenChange(v)}>
      <DialogContent data-testid={`sessions-dialog-${mode}`} className="max-w-lg">
        <DialogHeader>
          <DialogTitle
            className="text-xl tracking-tight font-bold"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            <span className="flex items-center gap-2">
              {isSave ? <Save className="w-5 h-5" /> : <FolderOpen className="w-5 h-5" />}
              {conflict ? refusalText(conflict, kind, name).title : title}
            </span>
          </DialogTitle>
          <DialogDescription>
            {isSave
              ? (kind === "session"
                  ? "Save this whole workspace — every activity tab — as a Session."
                  : "Save the current activity to the Library for reuse in future sessions.")
              : "Pick a saved Activity or Session to load."}
          </DialogDescription>
        </DialogHeader>

        {isSave && conflict ? (
          <div className="flex flex-col gap-3 py-2" role="alert" data-testid="save-conflict">
            <p className="text-sm font-semibold text-slate-900" data-testid="save-conflict-message">
              {refusalText(conflict, kind, name).message}
            </p>
            <p className="text-sm text-slate-600">{refusalText(conflict, kind, name).detail}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="save-conflict-cancel-btn">
                Cancel
              </Button>
              <Button onClick={handleSaveAsCopy} data-testid="save-conflict-copy-btn">
                Save as copy
              </Button>
            </div>
          </div>
        ) : isSave ? (
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="save-name" className="text-xs font-semibold text-slate-700">
                {kind === "session" ? "Session name" : "Activity name"}
              </Label>
              <Input
                id="save-name"
                data-testid="save-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={kind === "session" ? "e.g. Tuesday U14 training" : "e.g. Rondo 4v2"}
                autoFocus
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-1">
            <div className="flex items-center gap-1 p-0.5 rounded-md bg-slate-100 self-start">
              <button
                type="button"
                onClick={() => setLibraryKind("activity")}
                data-testid="library-kind-activity"
                className={
                  "px-3 py-1 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors " +
                  (libraryKind === "activity"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900")
                }
              >
                Activities
              </button>
              <button
                type="button"
                onClick={() => setLibraryKind("session")}
                data-testid="library-kind-session"
                className={
                  "px-3 py-1 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors " +
                  (libraryKind === "session"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900")
                }
              >
                Sessions
              </button>
            </div>

            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="library-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${libraryKind === "session" ? "sessions" : "activities"}…`}
                className="pl-9"
              />
            </div>

            <div className="max-h-[320px] overflow-y-auto -mx-6 px-6">
              {loading ? (
                <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
              ) : filteredList.length === 0 ? (
                <div
                  className="text-sm text-slate-500 py-6 text-center"
                  data-testid="library-empty-state"
                >
                  {libraryKind === "session"
                    ? "No saved sessions yet."
                    : "No saved activities yet."}
                </div>
              ) : (
                <ul className="flex flex-col gap-2" data-testid={`${libraryKind}-list`}>
                  {filteredList.map((a) => (
                    <li key={a.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleLoadClick(a.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleLoadClick(a.id);
                          }
                        }}
                        data-testid={`${libraryKind}-item-${a.id}`}
                        className="w-full flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 hover:bg-slate-50 transition-colors text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
                      >
                        <div className="flex flex-col min-w-0 gap-1">
                          <span className="text-sm font-semibold text-slate-900 truncate">
                            {a.name || "Untitled"}
                          </span>
                          <span className="text-xs text-slate-500 truncate">
                            {libraryKind === "session"
                              ? `${(a.activities || []).length} activit${(a.activities || []).length === 1 ? "y" : "ies"}`
                              : `${a.objects?.length || 0} objects · ${a.arrows?.length || 0} arrows · ${a.shapes?.length || 0} shapes`}
                            {" · "}
                            {a.updatedAt ? new Date(a.updatedAt).toLocaleDateString() : ""}
                          </span>
                        </div>
                        <div className="shrink-0 flex items-center gap-1">
                          {libraryKind === "activity" && isSessionOpen && (
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const res = await axios.get(`${API}/activities/${a.id}`);
                                  onAddActivityToSession && onAddActivityToSession(res.data);
                                  toast.success(`Added "${res.data.name || 'activity'}" to session`);
                                  onOpenChange(false);
                                } catch (err) {
                                  if (handleSignInExpired(err, "add to your session")) return;
                                  toast.error("Couldn't add to session");
                                }
                              }}
                              data-testid={`activity-add-to-session-${a.id}`}
                              className="text-slate-500 hover:text-slate-900 transition-colors p-1.5 rounded-md hover:bg-slate-100"
                              aria-label="Add to session"
                              title="Add to current session"
                            >
                              <ListPlus className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => handleDelete(a, e)}
                            data-testid={`${libraryKind}-delete-${a.id}`}
                            className="text-slate-400 hover:text-red-600 transition-colors p-1.5 rounded-md hover:bg-slate-100"
                            aria-label="Delete"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {!conflict && (
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="dialog-cancel-btn"
          >
            Cancel
          </Button>
          {isSave && (
            <Button onClick={handleSave} disabled={saving} data-testid="save-confirm-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
